import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Plus, ArrowLeft, Edit2 } from "lucide-react";
import type { Job, User } from "../types";
import { getDossierTypeLabel, getLightingTypeLabel } from "../utils/dossierLabels";
import { getJobDisplayStatus } from "../utils/jobStatus";
import { StatusBadge } from "./ui/StatusBadge";

const WEEKDAY_LABELS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const MONTH_LABELS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

/** Vert pour tous les RDV validés (terminés). Déshumidificateur en rose pastel. */
const STYLE_COMPLETED = "border-l-4 border-emerald-500 dark:border-emerald-400 bg-emerald-500/15 dark:bg-emerald-500/25 text-emerald-900 dark:text-emerald-100 hover:bg-emerald-500/22 dark:hover:bg-emerald-500/35";
const STYLE_DESHUMIDIFICATEUR = "border-l-4 border-pink-300 dark:border-pink-400 bg-pink-100/90 dark:bg-pink-500/20 text-pink-900 dark:text-pink-100 hover:bg-pink-200/90 dark:hover:bg-pink-500/30";

function getJobCardStyle(job: Job): string {
  const displayStatus = getJobDisplayStatus(job);
  const completed = displayStatus === "completed";
  const dossierType = String(job.dossierType ?? "").toLowerCase();
  const isDeshumidificateur = dossierType === "deshumidificateur" || dossierType === "vmc_agricoles";
  const isVT = job.lightingType === "visite technique";
  const scheduled = displayStatus === "scheduled";
  const inProgress = displayStatus === "in_progress";
  if (completed) return STYLE_COMPLETED;
  if (isDeshumidificateur) return STYLE_DESHUMIDIFICATEUR;
  if (isVT) {
    return "border-l-4 border-orange-500 dark:border-orange-400 bg-orange-500/15 dark:bg-orange-500/25 text-orange-900 dark:text-orange-100 hover:bg-orange-500/22 dark:hover:bg-orange-500/35";
  }
  if (inProgress) {
    return "border-l-4 border-amber-500 dark:border-amber-400 bg-amber-500/15 dark:bg-amber-500/25 text-amber-900 dark:text-amber-100 hover:bg-amber-500/22 dark:hover:bg-amber-500/35";
  }
  if (scheduled) {
    return "border-l-4 border-blue-500 dark:border-blue-400 bg-blue-500/15 dark:bg-blue-500/25 text-blue-900 dark:text-blue-100 hover:bg-blue-500/22 dark:hover:bg-blue-500/35";
  }
  return STYLE_COMPLETED;
}

export interface PlanningCalendarProps {
  jobs: Job[];
  onJobSelect?: (job: Job) => void;
  onUpdateJob?: (job: Job) => void;
  /** Appelé quand l'utilisateur demande d'ajouter un RDV pour une date (ex. depuis la vue jour agrandi). */
  onAddJob?: (date: string) => void;
  /** Dossiers sans date que le poseur peut assigner à un jour (clic sur jour → choisir un de ces jobs). */
  unassignedJobs?: Job[];
  /** Appelé quand le poseur assigne un job sans date à la date du jour ouvert. */
  onAssignJobToDate?: (date: string, job: Job) => void;
  installers?: User[];
  partners?: User[];
  readOnly?: boolean;
}

export function PlanningCalendar({
  jobs,
  onJobSelect,
  onAddJob,
  unassignedJobs = [],
  onAssignJobToDate,
  installers,
  partners = [],
  readOnly = false,
}: PlanningCalendarProps) {
  const [viewDate, setViewDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  /** Jour agrandi : affiche le détail du jour et le bouton "Ajouter un RDV". */
  const [expandedDate, setExpandedDate] = useState<string | null>(null);

  const jobsWithDate = useMemo(
    () => jobs.filter((j) => j.date && j.status !== "cancelled"),
    [jobs]
  );

  const jobsByDate = useMemo(() => {
    const map: Record<string, Job[]> = {};
    for (const job of jobsWithDate) {
      const d = job.date!;
      if (!map[d]) map[d] = [];
      map[d].push(job);
    }
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => (a.time || "").localeCompare(b.time || ""));
    }
    return map;
  }, [jobsWithDate]);

  const calendarDays = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const startDow = first.getDay();
    const monFirst = startDow === 0 ? 6 : startDow - 1;
    const totalDays = last.getDate();
    const prevMonth = new Date(year, month, 0);
    const prevTotal = prevMonth.getDate();
    const days: { date: string; isCurrentMonth: boolean; day: number }[] = [];

    for (let i = 0; i < monFirst; i++) {
      const d = prevTotal - monFirst + 1 + i;
      const prevDate = new Date(year, month - 1, d);
      days.push({
        date: `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, "0")}-${String(prevDate.getDate()).padStart(2, "0")}`,
        isCurrentMonth: false,
        day: prevDate.getDate(),
      });
    }
    for (let d = 1; d <= totalDays; d++) {
      days.push({
        date: `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
        isCurrentMonth: true,
        day: d,
      });
    }
    const remainder = 42 - days.length;
    for (let i = 0; i < remainder; i++) {
      const d = i + 1;
      const nextDate = new Date(year, month + 1, d);
      days.push({
        date: `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, "0")}-${String(nextDate.getDate()).padStart(2, "0")}`,
        isCurrentMonth: false,
        day: nextDate.getDate(),
      });
    }
    return days;
  }, [viewDate]);

  const prevMonth = () => {
    setViewDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setViewDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  };

  const getInstallerName = (installerId?: string) => {
    if (!installers || !installerId) return null;
    return installers.find((u) => u.id === installerId)?.name ?? installerId;
  };

  const getPartnerName = (partnerId?: string) => {
    if (!partners || !partnerId) return null;
    const partner = partners.find((u) => u.id === partnerId);
    return partner?.company ?? partner?.name ?? partnerId;
  };

  const isToday = (dateStr: string) => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, "0");
    const d = String(today.getDate()).padStart(2, "0");
    return dateStr === `${y}-${m}-${d}`;
  };

  const formatDayLabel = (dateStr: string) => {
    try {
      return new Date(dateStr + "T12:00:00").toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const handleDayCellClick = (dateStr: string) => {
    setExpandedDate(dateStr);
  };

  const expandedDayJobs = expandedDate ? (jobsByDate[expandedDate] ?? []) : [];

  return (
    <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-white dark:bg-slate-800/80 shadow-[0_2px_24px_-4px_rgba(0,0,0,0.08),0_8px_16px_-6px_rgba(0,0,0,0.04)] dark:shadow-[0_2px_24px_-4px_rgba(0,0,0,0.3),0_8px_16px_-6px_rgba(0,0,0,0.2)] border border-gray-100/80 dark:border-white/[0.06] backdrop-blur-xl">
      <div className="flex items-center justify-between px-3 py-3 sm:px-6 sm:py-5">
        <button
          type="button"
          onClick={prevMonth}
          className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-full text-gray-400 transition-all duration-200 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/10 dark:hover:text-gray-200 active:scale-95 touch-manipulation"
          aria-label="Mois précédent"
        >
          <ChevronLeft size={20} strokeWidth={2} className="sm:w-[22px] sm:h-[22px]" />
        </button>
        <h2 className="text-[13px] sm:text-[17px] font-semibold tracking-tight text-gray-900 dark:text-white truncate mx-1">
          {MONTH_LABELS[viewDate.getMonth()]} {viewDate.getFullYear()}
        </h2>
        <button
          type="button"
          onClick={nextMonth}
          className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-full text-gray-400 transition-all duration-200 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/10 dark:hover:text-gray-200 active:scale-95 touch-manipulation"
          aria-label="Mois suivant"
        >
          <ChevronRight size={20} strokeWidth={2} className="sm:w-[22px] sm:h-[22px]" />
        </button>
      </div>

      <div className="overflow-x-auto overscroll-x-contain -mx-1 px-1 sm:mx-0 sm:px-0">
        <div className="min-w-[280px] w-full px-2 pb-4 pt-0 sm:px-4 sm:pb-5">
          <div className="grid grid-cols-7 gap-px mb-1.5 sm:mb-3">
            {WEEKDAY_LABELS.map((label) => (
              <div
                key={label}
                className="text-center text-[9px] sm:text-[11px] font-medium uppercase tracking-wider sm:tracking-widest text-gray-400 dark:text-gray-500 py-1 sm:py-2"
              >
                {label}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-0.5 sm:gap-1.5">
            {calendarDays.map((cell) => {
              const dayJobs = jobsByDate[cell.date] ?? [];
              const today = isToday(cell.date);
              const mobileDots = Math.min(dayJobs.length, 3);
              const overflowCount = dayJobs.length - mobileDots;
              return (
                <div
                  key={cell.date}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleDayCellClick(cell.date)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleDayCellClick(cell.date);
                    }
                  }}
                  className={`group min-h-[48px] sm:min-h-[80px] rounded-lg sm:rounded-xl p-0.5 sm:p-1.5 cursor-pointer transition-all duration-200 ease-out touch-manipulation ${
                    cell.isCurrentMonth
                      ? "bg-gray-50/80 dark:bg-white/[0.03] hover:bg-gray-100/90 dark:hover:bg-white/[0.06] active:scale-[0.98]"
                      : "bg-transparent hover:bg-gray-50/50 dark:hover:bg-white/[0.02]"
                  }`}
                >
                  <div
                    className={`flex h-4 w-4 sm:h-6 sm:w-6 items-center justify-center rounded-full text-[10px] sm:text-[12px] font-medium transition-colors mb-0.5 sm:mb-1 mx-auto ${
                      cell.isCurrentMonth
                        ? today
                          ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                          : "text-gray-700 dark:text-gray-200 group-hover:bg-gray-200/80 dark:group-hover:bg-white/10"
                        : "text-gray-300 dark:text-gray-600"
                    }`}
                  >
                    {cell.day}
                  </div>
                  <div className="sm:hidden flex items-center justify-center gap-0.5 min-h-[8px]">
                    {mobileDots > 0 ? (
                      <>
                        {Array.from({ length: mobileDots }).map((_, index) => (
                          <span
                            key={`${cell.date}-dot-${index}`}
                            className="h-1 w-1 rounded-full bg-gray-400/80 dark:bg-gray-500/80"
                          />
                        ))}
                        {overflowCount > 0 && (
                          <span className="text-[8px] text-gray-400 dark:text-gray-500">+{overflowCount}</span>
                        )}
                      </>
                    ) : (
                      <span className="text-[8px] opacity-0">.</span>
                    )}
                  </div>
                  <div className="hidden sm:block space-y-0.5 overflow-hidden max-h-[48px] overflow-y-auto">
                  {dayJobs.map((job) => {
                    const installerName = getInstallerName(job.installerId);
                    const partnerName = getPartnerName(job.partnerId);
                    return (
                      <button
                        key={job.id}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onJobSelect?.(job);
                        }}
                        className={`w-full text-left rounded-lg px-1.5 py-0.5 text-[10px] leading-tight transition-all duration-150 ${getJobCardStyle(job)} ${!readOnly ? "cursor-pointer" : "cursor-default"}`}
                        title={`${job.clientName}${job.time ? ` · ${job.time}` : ""}${installerName ? ` · ${installerName}` : ""}${partnerName ? ` · ${partnerName}` : ""}`}
                      >
                        <div className="flex items-center gap-1 min-w-0">
                          <span className="truncate font-medium opacity-95 flex-1">{job.clientName}</span>
                          <span className="shrink-0 scale-75 origin-right">
                            <StatusBadge status={job.status} installerId={job.installerId} />
                          </span>
                        </div>
                        {(job.time || installerName || partnerName) && (
                          <span className="block truncate text-[9px] opacity-80">
                            {[job.time, installerName, partnerName].filter(Boolean).join(" · ")}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
          </div>
        </div>
      </div>

      {expandedDate &&
        typeof document !== "undefined" &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-50 bg-black/40 dark:bg-black/55 backdrop-blur-md animate-in fade-in duration-300 ease-out"
              aria-hidden
              onClick={() => setExpandedDate(null)}
            />
            <div
              className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 pointer-events-none"
              aria-modal
              aria-labelledby="day-page-title"
            >
              <div
                className="pointer-events-auto w-full max-w-md max-h-[90vh] sm:max-h-[85vh] flex flex-col rounded-t-3xl sm:rounded-3xl bg-white dark:bg-slate-800/95 shadow-[0_24px_80px_-24px_rgba(0,0,0,0.2),0_0_0_1px_rgba(0,0,0,0.05)] dark:shadow-[0_24px_80px_-24px_rgba(0,0,0,0.5),0_0_0_1px_rgba(255,255,255,0.06)] border border-gray-200/60 dark:border-white/[0.08] backdrop-blur-xl animate-in slide-in-from-bottom duration-300 ease-out sm:zoom-in-95 sm:fade-in pb-[env(safe-area-inset-bottom,0px)]"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-2 sm:gap-3 px-4 sm:px-5 pt-3 sm:pt-5 pb-2 sm:pb-3 shrink-0 border-b border-gray-100 dark:border-white/5 sm:border-0">
                  <button
                    type="button"
                    onClick={() => setExpandedDate(null)}
                    className="flex items-center justify-center gap-2 rounded-full py-2.5 pl-2 pr-3 text-[15px] font-medium text-gray-500 transition-all duration-200 hover:bg-gray-100 hover:text-gray-800 dark:hover:bg-white/10 dark:hover:text-gray-200 active:scale-[0.98] touch-manipulation w-full sm:w-auto"
                  >
                    <ArrowLeft size={20} strokeWidth={2} /> Retour
                  </button>
                  {onAddJob && (
                    <button
                      type="button"
                      onClick={() => {
                        onAddJob(expandedDate);
                        setExpandedDate(null);
                      }}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-4 py-2.5 text-[14px] font-semibold shadow-lg shadow-gray-900/20 dark:shadow-black/30 transition-all duration-200 hover:opacity-90 active:scale-[0.98] touch-manipulation w-full sm:w-auto"
                    >
                      <Plus size={18} strokeWidth={2.5} /> Ajouter un RDV
                    </button>
                  )}
                </div>
                <h2
                  id="day-page-title"
                  className="px-4 sm:px-5 pt-2 sm:pt-1 pb-3 sm:pb-4 text-[18px] sm:text-[22px] font-semibold tracking-tight text-gray-900 dark:text-white capitalize shrink-0"
                >
                  {formatDayLabel(expandedDate)}
                </h2>
                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 sm:px-5 pb-6 space-y-4">
                  <div className="space-y-2.5">
                    {expandedDayJobs.length === 0 ? (
                      <div className="py-8 text-center">
                        <p className="text-[15px] text-gray-400 dark:text-gray-500 leading-relaxed">
                          Aucun dossier ce jour-là.
                        </p>
                        {onAddJob && (
                          <p className="mt-1 text-[13px] text-gray-400 dark:text-gray-500">
                            Utilisez « Ajouter un RDV » pour en créer un.
                          </p>
                        )}
                      </div>
                    ) : (
                      expandedDayJobs.map((job) => {
                        const installerName = getInstallerName(job.installerId);
                        const partnerName = getPartnerName(job.partnerId);
                        return (
                          <button
                            key={job.id}
                            type="button"
                            onClick={() => {
                              onJobSelect?.(job);
                              setExpandedDate(null);
                            }}
                            className={`w-full text-left rounded-2xl px-4 py-4 flex items-start justify-between gap-3 transition-all duration-200 ${getJobCardStyle(job)} ${!readOnly ? "cursor-pointer active:scale-[0.995]" : "cursor-default"}`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-[15px] font-semibold tracking-tight">{job.clientName}</span>
                                <StatusBadge status={job.status} installerId={job.installerId} />
                                <span className="text-[13px] text-gray-500 dark:text-gray-400">{job.time || "—"}</span>
                              </div>
                              {job.clientAddress && (
                                <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-0.5 truncate">{job.clientAddress}</p>
                              )}
                              {(getDossierTypeLabel(job.dossierType) || getLightingTypeLabel(job.lightingType)) && (
                                <p className="text-[12px] font-medium mt-1 opacity-90">
                                  {[getDossierTypeLabel(job.dossierType), getLightingTypeLabel(job.lightingType)]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </p>
                              )}
                              {installerName && (
                                <p className="text-[12px] text-gray-400 dark:text-gray-500 mt-1">{installerName}</p>
                              )}
                              {partnerName && (
                                <p className="text-[12px] text-gray-400 dark:text-gray-500 mt-0.5">
                                  Partenaire: {partnerName}
                                </p>
                              )}
                            </div>
                            {!readOnly && onJobSelect && (
                              <span className="shrink-0 flex h-9 w-9 items-center justify-center rounded-xl bg-black/5 dark:bg-white/10 text-gray-500 dark:text-gray-400" title="Modifier">
                                <Edit2 size={16} strokeWidth={2} />
                              </span>
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>

                  {unassignedJobs.length > 0 && onAssignJobToDate && (
                    <div className="pt-2 border-t border-gray-200 dark:border-white/10">
                      <p className="text-[12px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
                        Assigner une installation à ce jour
                      </p>
                      <div className="space-y-2">
                        {unassignedJobs.map((job) => (
                          <button
                            key={job.id}
                            type="button"
                            onClick={() => onAssignJobToDate(expandedDate, job)}
                            className="w-full text-left rounded-2xl px-4 py-3 border-l-4 border-amber-500 dark:border-amber-400 bg-amber-500/15 dark:bg-amber-500/25 text-amber-900 dark:text-amber-100 hover:bg-amber-500/22 dark:hover:bg-amber-500/35 transition-all duration-200 active:scale-[0.995]"
                          >
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-[14px] font-medium">{job.clientName}</span>
                              <StatusBadge status={job.status} installerId={job.installerId} />
                            </div>
                            {job.clientAddress && (
                              <p className="text-[12px] text-amber-700/80 dark:text-amber-200/80 mt-0.5 truncate">{job.clientAddress}</p>
                            )}
                            {(getDossierTypeLabel(job.dossierType) || getLightingTypeLabel(job.lightingType)) && (
                              <p className="text-[11px] font-medium mt-1 opacity-90">
                                {[getDossierTypeLabel(job.dossierType), getLightingTypeLabel(job.lightingType)]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </p>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
}
